import { useEffect, useState } from 'react';
import {
  commonAreaReservationService,
  CommonAreaBooking,
} from '../../services/commonAreaReservationService';

/**
 * What the calendar of one área needs: the bookings around today and the dates blocked by the
 * síndico. Both booking screens read it, so the portaria's modal and the síndico's page cannot
 * paint the same month differently.
 *
 * The window is 90 days from today, in one request rather than one per date the operator tries —
 * enough to cover any `max_advance_days` a condomínio actually configures.
 *
 * Best-effort: the server validates every conflict again on create, so a failed read costs the
 * operator a 409 instead of letting a double booking through.
 */
export function useAreaCalendarData(areaId: string | null | undefined, today: Date) {
  const [bookings, setBookings] = useState<CommonAreaBooking[]>([]);
  const [blackouts, setBlackouts] = useState<string[]>([]);

  // Depend on the DAY, not the Date instance: a caller that builds `new Date()` on every render
  // would otherwise refetch forever.
  const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;

  useEffect(() => {
    if (!areaId) {
      setBookings([]);
      setBlackouts([]);
      return;
    }
    let cancelled = false;
    const [y, m, d] = todayKey.split('-').map(Number);
    const anchor = new Date(y, m, d);
    const iso = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    // RFC3339, because `GetBookingsForArea` parses these with `time.Parse(time.RFC3339, ...)` and
    // on failure just leaves the filter unset — a date-only `from` quietly fetches every booking
    // the área has ever had instead of narrowing anything.
    commonAreaReservationService.getBookingsForArea(areaId, {
      from: `${iso(anchor)}T00:00:00Z`,
      to: `${iso(new Date(y, m, d + 90))}T23:59:59Z`,
    })
      .then(list => { if (!cancelled) setBookings(list); })
      .catch(() => { if (!cancelled) setBookings([]); });

    commonAreaReservationService.getBlackoutDates(areaId)
      .then(list => { if (!cancelled) setBlackouts(list.map(b => (b.blackout_date || '').slice(0, 10))); })
      .catch(() => { if (!cancelled) setBlackouts([]); });

    return () => { cancelled = true; };
  }, [areaId, todayKey]);

  return { bookings, blackouts };
}

export default useAreaCalendarData;
